/* eslint-disable react/prop-types */
import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { HiArrowLeft } from 'react-icons/hi'
import { postRequest } from '../../utility/apiRequest'

const decodeSafe = (value: string) => {
    try {
        return decodeURIComponent(value)
    } catch {
        return value
    }
}

const extractResetToken = (location: { search: string; pathname: string; hash: string }) => {
    const params = new URLSearchParams(location.search)
    const fromNamedQuery = params.get('token') || params.get('resetToken') || params.get('reset_token')
    if (fromNamedQuery) return decodeSafe(fromNamedQuery.trim())

    // Supports links like /reset-password?RAW_TOKEN_WITHOUT_KEY
    let rawQueryToken = ''
    params.forEach((value, key) => {
        if (!rawQueryToken && !value && key) {
            rawQueryToken = decodeSafe(key.trim())
        }
    })
    if (rawQueryToken) return rawQueryToken

    // Supports links like /reset-password/<token>
    const pathParts = location.pathname.split('/').filter(Boolean)
    const resetIndex = pathParts.findIndex((part) => part === 'reset-password')
    if (resetIndex >= 0 && pathParts[resetIndex + 1]) {
        return decodeSafe(pathParts[resetIndex + 1].trim())
    }

    // Supports hash-based links like /reset-password#token=...
    if (location.hash) {
        const hashParams = new URLSearchParams(location.hash.replace(/^#/, ''))
        const fromHash = hashParams.get('token') || hashParams.get('resetToken') || hashParams.get('reset_token')
        if (fromHash) return decodeSafe(fromHash.trim())
    }

    return ''
}

const ResetPassword: React.FC = () => {
    const [hideOrShowPassword, setHideOrShowPassword] = useState<boolean>(false)
    const [hideOrShowConfirmPassword, setHideOrShowConfirmPassword] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const [response, setResponse] = useState('')
    const [userResponseType, setUserResponseType] = useState<boolean>(false)
    const navigate = useNavigate()
    const location = useLocation()
    const tokenFromQuery = extractResetToken(location)
    const loginPageUrl = process.env.REACT_APP_LOGIN_PAGE_URL?.trim() || '/login'

    const redirectToLogin = () => {
        if (/^https?:\/\//i.test(loginPageUrl)) {
            window.location.assign(loginPageUrl)
            return
        }

        navigate(loginPageUrl)
    }

    const validationSchema = Yup.object().shape({
        password: Yup.string()
            .required('Password is required')
            .min(8, 'Password must be at least 8 characters')
            .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
            .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
            .matches(/[0-9]/, 'Password must contain at least one number'),
        confirmPassword: Yup.string()
            .required('Confirm password is required')
            .oneOf([Yup.ref('password')], 'Password confirmation does not match')
    })

    const handleResetPassword = async (values: { password: string; confirmPassword: string }) => {
        if (!tokenFromQuery) {
            setUserResponseType(false)
            setResponse('Invalid or expired reset token')
            return
        }

        setLoading(true)
        setResponse('')
        setUserResponseType(false)

        const payload = {
            token: tokenFromQuery,
            password: values.password,
            confirmPassword: values.confirmPassword
        }

        const result = await postRequest('auth/reset-password', payload)
        if (result.data.success === true) {
            setLoading(false)
            setUserResponseType(true)
            setResponse(result.data.message || 'Password reset successfully.')
            setTimeout(() => {
                redirectToLogin()
            }, 1500)
            return
        }

        setLoading(false)
        setUserResponseType(false)
        setResponse(result?.data?.message || 'Encountered an error. Please try again.')
    }

    return (
        <>
            <div className="verification py-5">
                <div className="container py-5">
                    <div className="row justify-content-center align-items-center text-center ">
                        <div className="col">
                            <img src="/assets/lock.png" alt="logo" className="img-fluid mb-4" width={'60px'} />
                        </div>
                        <h3 className="signup__col--title">Reset password</h3>
                        <p className="pb-5 signup__col--text">Set your new password.</p>
                        <div className="col-md-8  mb-3">
                            {response && <p className={userResponseType ? 'alert alert-success' : 'alert alert-danger'}>{response}</p>}
                        </div>
                        <Formik
                            initialValues={{ password: '', confirmPassword: '' }}
                            validationSchema={validationSchema}
                            onSubmit={handleResetPassword}
                        >
                            {(props) => {
                                const { touched, errors, dirty } = props
                                return (
                                    <div className="col-md-5">
                                        <Form>
                                            <div className="mb-4 text-start position-relative">
                                                <label htmlFor="password" className="signup__col--label">
                                                    New Password
                                                </label>
                                                <Field
                                                    name="password"
                                                    id="password"
                                                    type={hideOrShowPassword ? 'text' : 'password'}
                                                    placeholder="Enter new password"
                                                    className={
                                                        touched.password && errors.password
                                                            ? 'form-control px-3 signup__col--inpisIvalid shadow-none'
                                                            : 'form-control px-3 signup__col--inp shadow-none'
                                                    }
                                                />
                                                <ErrorMessage name="password" component="div" className="error text-danger signup__col--label" />
                                                <i
                                                    className={hideOrShowPassword ? 'fa-regular fa-eye signup__col--icon' : 'fa-regular fa-eye-slash signup__col--icon'}
                                                    onClick={() => setHideOrShowPassword(!hideOrShowPassword)}
                                                />
                                            </div>
                                            <div className="mb-4 text-start position-relative">
                                                <label htmlFor="confirmPassword" className="signup__col--label">
                                                    Confirm Password
                                                </label>
                                                <Field
                                                    name="confirmPassword"
                                                    id="confirmPassword"
                                                    type={hideOrShowConfirmPassword ? 'text' : 'password'}
                                                    placeholder="Confirm new password"
                                                    className={
                                                        touched.confirmPassword && errors.confirmPassword
                                                            ? 'form-control px-3 signup__col--inpisIvalid shadow-none'
                                                            : 'form-control px-3 signup__col--inp shadow-none'
                                                    }
                                                />
                                                <ErrorMessage name="confirmPassword" component="div" className="error text-danger signup__col--label" />
                                                <i
                                                    className={
                                                        hideOrShowConfirmPassword
                                                            ? 'fa-regular fa-eye signup__col--icon'
                                                            : 'fa-regular fa-eye-slash signup__col--icon'
                                                    }
                                                    onClick={() => setHideOrShowConfirmPassword(!hideOrShowConfirmPassword)}
                                                />
                                            </div>
                                            <div className="mb-3 py-4">
                                                {loading ? (
                                                    <button type="button" className="link btn fw-bold py-3 px-5 me-0 signup__col--btn" disabled>
                                                        <div className="spinner-border spinner-border-sm" role="status">
                                                            <span className="visually-hidden">Loading...</span>
                                                        </div>
                                                    </button>
                                                ) : (
                                                    <button className={!(dirty && touched) ? 'disabled-btn signup__col--btn' : 'signup__col--btn'} type="submit">
                                                        Reset Password
                                                    </button>
                                                )}
                                            </div>
                                            <div className=" text-center">
                                                <p className="signup__col--label">
                                                    <a href={loginPageUrl} style={{ color: '#2C4A8B', textDecoration: 'none', fontWeight: '600', fontSize: '14px' }}>
                                                        <HiArrowLeft className="me-1" fontSize={18} /> Back to log in
                                                    </a>
                                                </p>
                                            </div>
                                        </Form>
                                    </div>
                                )
                            }}
                        </Formik>
                    </div>
                </div>
            </div>
        </>
    )
}
export default ResetPassword
